import React, { startTransition, useDeferredValue, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SearchIcon from "@mui/icons-material/Search";
import Nav from "./Nav";
import Footer from "./Footer";
import QuickView from "./QuickView";
import Row from "./Row";
import "./Search.scss";
import { useAuth } from "../context/AuthContext";
import {
  buildMediaUrl,
  getDirectPlayPath,
  getTitlePath,
  searchStreamingCatalog,
} from "../services/streamApi";

function SearchPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get("q") || "");
  const deferredQuery = useDeferredValue(query);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState(null);

  useEffect(() => {
    const value = searchParams.get("q") || "";
    if (value !== query) {
      setQuery(value);
    }
  }, [searchParams, query]);

  useEffect(() => {
    const timeoutId = window.setTimeout(async () => {
      try {
        setLoading(true);
        const payload = await searchStreamingCatalog({
          query: deferredQuery,
          userId: user?.userId,
        });

        startTransition(() => {
          setResults(payload);
        });
      } catch (error) {
        console.error("Erreur recherche:", error);
      } finally {
        setLoading(false);
      }
    }, 220);

    return () => window.clearTimeout(timeoutId);
  }, [deferredQuery, user?.userId]);

  const handleChange = (value) => {
    setQuery(value);

    if (value.trim()) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="app-shell">
      <Nav />

      <main className="search-page">
        <section className="search-page__hero">
          <div className="search-page__bar">
            <SearchIcon />
            <input
              type="search"
              placeholder="Rechercher un film, une serie, un documentaire..."
              value={query}
              onChange={(event) => handleChange(event.target.value)}
            />
          </div>
          <p className="search-page__hint">
            Recherche intelligente par titre, type, categorie et popularite comme sur une plateforme de streaming moderne.
          </p>
        </section>

        {results?.topResult ? (
          <section className="search-page__topResult">
            <div className="search-page__topPoster">
              <img src={buildMediaUrl(results.topResult.backdropPath || results.topResult.posterPath)} alt={results.topResult.title} />
            </div>

            <div className="search-page__topContent">
              <p className="search-page__eyebrow">Top resultat</p>
              <h1>{results.topResult.title}</h1>
              <div className="search-page__meta">
                <span>{results.topResult.type}</span>
                {results.topResult.categoryLabel ? <span>{results.topResult.categoryLabel}</span> : null}
                {results.topResult.durationLabel ? <span>{results.topResult.durationLabel}</span> : null}
              </div>
              <p>{results.topResult.description}</p>
              <div className="search-page__cta">
                <Link to={getDirectPlayPath(results.topResult)} className="search-page__button search-page__button--play">
                  <PlayArrowIcon /> Lecture
                </Link>
                <button className="search-page__button" onClick={() => setSelectedTitle(results.topResult)}>
                  Plus d&apos;infos
                </button>
              </div>
            </div>
          </section>
        ) : null}

        {loading ? (
          <section className="page-placeholder">
            <div className="page-placeholder__row" />
            <div className="page-placeholder__row" />
          </section>
        ) : null}

        {!loading && results?.suggestions?.length ? (
          <section className="search-page__suggestions">
            <h2>Suggestions rapides</h2>
            <div className="search-page__chips">
              {results.suggestions.map((item) => (
                <Link key={item.id} to={getTitlePath(item.id)} className="search-page__chip">
                  {item.title}
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        {!loading && results?.grouped ? (
          <>
            <Row title="Tous les resultats" subtitle={`${results.grouped.all.length} titre(s) trouves`} items={results.grouped.all} onMoreInfo={setSelectedTitle} />
            <Row title="Films" items={results.grouped.films} onMoreInfo={setSelectedTitle} />
            <Row title="Series" items={results.grouped.series} onMoreInfo={setSelectedTitle} />
            <Row title="Documentaires" items={results.grouped.documentaires} onMoreInfo={setSelectedTitle} />
          </>
        ) : null}
      </main>

      <Footer />
      <QuickView movie={selectedTitle} popup={() => setSelectedTitle(null)} popupStatut={Boolean(selectedTitle)} />
    </div>
  );
}

export default SearchPage;
